import { ChangeDetectionStrategy, Component, DestroyRef, effect, inject, OnInit, signal, viewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { ActivatedRoute, NavigationEnd, Router, RouterModule, RouterOutlet } from '@angular/router';
import { TranslocoModule } from '@jsverse/transloco';
import { MenuItem } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { MenuModule } from 'primeng/menu';
import { TabsModule } from 'primeng/tabs';
import { TagModule } from 'primeng/tag';
import { filter } from 'rxjs/operators';
import { HasPermissionDirective } from '../../../../../directives/has-permission.directive';
import { CesAssetBreadcrumbComponent } from '../../../../ui/ces-asset-breadcrumb/ces-asset-breadcrumb.component';
import { CesMenuButtonSecondaryComponent } from '../../../../ui/ces-buttons/ces-menu-button-secondary.component';
import { CesCampaignTagsComponent } from '../../../../ui/ces-campaign-tags/ces-campaign-tags.component';
import { CesTitleComponent } from '../../../../ui/ces-route-title/ces-route-title.component';
import { CesStatusDotComponent } from '../../../../ui/ces-status-dot/ces-status-dot.component';
import { CampaignActionsService } from './sub-services/campaign-actions.service';
import { CampaignNavigationService } from './sub-services/campaign-navigation.service';
import { CampaignPageStore } from './sub-services/campaign-page.store';
import { registerWsUpdateRefresh } from '../../../../../utils/measurement-update-refresh';
import { campaignUpdateTargetsCampaign } from '../../../../../utils/websocket-match';
import { CampaignUpdatePayload, WebSocketMessageType } from '../../../../../types/websocket.types';
import { CAMPAIGN_TAB_ROUTES, campaignTabFromUrl } from './campaign-tab.utils';

@Component({
  selector: 'ces-campaign',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    FormsModule,
    RouterModule,
    TabsModule,
    ButtonModule,
    MenuModule,
    TranslocoModule,
    CesAssetBreadcrumbComponent,
    CesMenuButtonSecondaryComponent,
    CesCampaignTagsComponent,
    CesTitleComponent,
    TagModule,
    CesStatusDotComponent,
    HasPermissionDirective,
  ],
  templateUrl: './campaign-details.component.html',
  styleUrl: './campaign-details.component.scss',
})
export class CesCampaignDetailsComponent implements OnInit {
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly destroyRef = inject(DestroyRef);

  private readonly tabRoutes = CAMPAIGN_TAB_ROUTES;
  readonly activeIndex = signal<number>(0);
  readonly campaignMenuItems = signal<MenuItem[]>([]);

  private readonly actions = inject(CampaignActionsService);
  readonly pageStore = inject(CampaignPageStore);
  readonly nav = inject(CampaignNavigationService);

  readonly outlet = viewChild(RouterOutlet);

  constructor() {
    // Live: a CAMPAIGN_UPDATE for this campaign re-fetches only the entity (status,
    // conflict/imported flags) — updating the dot/tags/menu and the FINISHED read-only lock
    // without resetting the tab or clobbering in-progress edits. Debounce coalesces a burst.
    const wsRefresh = registerWsUpdateRefresh<CampaignUpdatePayload>(WebSocketMessageType.CAMPAIGN_UPDATE, {
      targets: payload => campaignUpdateTargetsCampaign(payload, this.nav.currentCampaignId()),
      refresh: () => void this.pageStore.refreshCurrentCampaignEntity(),
      debounceMs: 500,
    });
    // Reconcile the entity after a socket outage — a change emitted while disconnected
    // would otherwise leave a stale status/conflict badge until manual navigation.
    wsRefresh.startReconnectCatchup();

    effect(() => {
      const campaign = this.pageStore.currentCampaign();
      if (campaign) {
        void this.loadCampaignMenuItems(campaign.id);
      }
    });
  }

  ngOnInit(): void {
    void this.initialize();
  }

  private async initialize(): Promise<void> {
    const initialTab = campaignTabFromUrl(this.router.url);
    this.activeIndex.set(this.tabRoutes.indexOf(initialTab));

    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((event: NavigationEnd) => {
        const tab = campaignTabFromUrl(event.url);
        this.activeIndex.set(this.tabRoutes.indexOf(tab));
      });

    const campaignId = this.nav.currentCampaignId();
    if (campaignId) {
      try {
        await this.pageStore.loadCampaignData(campaignId, initialTab);
      } catch (error) {
        if (error instanceof HttpErrorResponse && error.status === 404) {
          void this.router.navigate(['/404']);
        }
      }
    }
  }

  private async loadCampaignMenuItems(campaignId: number): Promise<void> {
    const campaign = this.pageStore.currentCampaign();
    if (!campaign) return;

    try {
      const items = await this.nav.getFilteredCampaignMenuItems(campaign);
      this.campaignMenuItems.set(items);
    } catch (error) {
      console.error(`Failed to load menu items for campaign ${campaignId}:`, error);
      this.campaignMenuItems.set([]);
    }
  }

  hasUnsavedChanges(): boolean {
    return this.pageStore.hasUnsavedChanges();
  }

  hasUnsavedAuscultationChanges(): boolean {
    return this.pageStore.hasUnsavedAuscultation();
  }

  hasUnsavedMeasuresChanges(): boolean {
    return this.pageStore.hasUnsavedMeasures();
  }

  cancelChanges() {
    const component = this.outlet()?.component as Partial<{ onCancel: () => void }> | undefined;
    component?.onCancel?.();
  }

  onTabChange(index: number) {
    const tabRoute = this.tabRoutes[index];
    if (!tabRoute) return;
    this.activeIndex.set(index);
    void this.router.navigate([tabRoute], { relativeTo: this.route });
    void this.pageStore.switchToTab(tabRoute);
  }

  onResolveConflicts(): void {
    const campaign = this.pageStore.currentCampaign();
    if (campaign) {
      void this.actions.resolveImportConflicts(campaign);
    }
  }
}
